// src/components/books/modals/ActionModal.jsx
import React, { useState } from 'react';
import { X, HelpCircle, ArrowRight } from 'lucide-react';

export const ActionModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentValue = '', 
  availableMessages = [] 
}) => {
  const [actionType, setActionType] = useState('');
  const [parameters, setParameters] = useState('');
  const [builtAction, setBuiltAction] = useState(currentValue);
  const [showHelper, setShowHelper] = useState(false);

  const actionTypes = [
    {
      value: 'jump',
      label: 'Jump to Message',
      description: 'Navigate to a specific message',
      syntax: 'jump:messageId',
      example: 'jump:5',
      parameters: 'messageId'
    },
    {
      value: 'jumpIf',
      label: 'Conditional Jump',
      description: 'Jump only if condition is met',
      syntax: 'jumpIf:condition:messageId',
      example: 'jumpIf:score>80:10',
      parameters: 'condition:messageId'
    },
    {
      value: 'variable',
      label: 'Set Variable',
      description: 'Set a variable to a specific value',
      syntax: 'variable:name:value',
      example: 'variable:userLevel:advanced',
      parameters: 'variableName:value'
    },
    {
      value: 'calculate',
      label: 'Calculate',
      description: 'Perform mathematical calculations',
      syntax: 'calculate:variable:expression',
      example: 'calculate:total:score1+score2',
      parameters: 'resultVariable:expression'
    },
    {
      value: 'redirect',
      label: 'Redirect URL',
      description: 'Redirect to external URL',
      syntax: 'redirect:url',
      example: 'redirect:https://example.com',
      parameters: 'url'
    },
    {
      value: 'email',
      label: 'Send Email',
      description: 'Trigger email notification',
      syntax: 'email:template:recipient',
      example: 'email:welcome:user@email.com',
      parameters: 'template:recipient'
    },
    {
      value: 'webhook',
      label: 'Call Webhook',
      description: 'Send data to external service',
      syntax: 'webhook:url:data',
      example: 'webhook:https://api.com/submit:userData',
      parameters: 'webhookUrl:dataToSend'
    },
    {
      value: 'analytics',
      label: 'Track Event',
      description: 'Track custom analytics event',
      syntax: 'analytics:eventName:data',
      example: 'analytics:quiz_completed:score',
      parameters: 'eventName:eventData'
    }
  ];

  const combinedActions = [
    'jump:5 && variable:level:2',
    'jumpIf:score>80:10 || jump:8',
    'variable:attempts:1 && analytics:quiz_start',
    'calculate:total:q1+q2+q3 && jumpIf:total>15:success_page'
  ];

  const buildAction = () => {
    const selectedType = actionTypes.find(type => type.value === actionType);
    if (!selectedType || !parameters) return;

    const action = `${actionType}:${parameters}`;
    
    if (builtAction && action) {
      setBuiltAction(builtAction + ' && ' + action);
    } else {
      setBuiltAction(action);
    }
    
    // Reset form
    setActionType('');
    setParameters('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Action Builder</h2>
            <button
              onClick={() => setShowHelper(!showHelper)}
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Helper Guide */}
          {showHelper && (
            <div className="bg-purple-50 rounded-lg p-4">
              <h3 className="font-medium text-purple-900 mb-2">How Actions Work</h3>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Actions execute when user completes a message</li>
                <li>• Combine multiple actions with && (all execute) or || (first success)</li>
                <li>• Actions run in order from left to right</li>
                <li>• Use variables from previous responses in parameters</li>
              </ul>
            </div>
          )}

          {/* Current Action */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Action
            </label>
            <textarea
              value={builtAction}
              onChange={(e) => setBuiltAction(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 font-mono text-sm"
              rows="3"
              placeholder="Enter action or use the builder below..."
            />
          </div>

          {/* Action Builder */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-medium mb-4">Visual Action Builder</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Action Type Selection */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Action Type
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {actionTypes.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setActionType(type.value)}
                      className={`p-3 text-left border rounded-lg hover:bg-gray-50 ${
                        actionType === type.value 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="font-medium text-sm">{type.label}</div>
                      <div className="text-xs text-gray-600 mt-1">{type.description}</div>
                      <div className="text-xs text-purple-600 mt-1 font-mono">{type.syntax}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Parameters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parameters
                </label>
                {actionType && (
                  <div className="space-y-3">
                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <strong>Format:</strong> {actionTypes.find(t => t.value === actionType)?.parameters}
                    </div>
                    
                    {/* Special parameter builders */}
                    {actionType === 'jump' && (
                      <select
                        value={parameters}
                        onChange={(e) => setParameters(e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="">Select message...</option>
                        {availableMessages.map(msg => (
                          <option key={msg.messageId} value={msg.messageId}>
                            #{msg.messageId} - {msg.section}
                          </option>
                        ))}
                      </select>
                    )}
                    
                    {actionType === 'jumpIf' && (
                      <div className="space-y-2">
                        <input
                          type="text"
                          placeholder="condition (e.g., score>80)"
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                          onChange={(e) => {
                            const condition = e.target.value;
                            const messageId = parameters.split(':')[1] || '';
                            setParameters(`${condition}:${messageId}`);
                          }}
                        />
                        <select
                          onChange={(e) => {
                            const condition = parameters.split(':')[0] || '';
                            setParameters(`${condition}:${e.target.value}`);
                          }}
                          className="w-full border border-gray-300 rounded px-3 py-2"
                        >
                          <option value="">Select target message...</option>
                          {availableMessages.map(msg => (
                            <option key={msg.messageId} value={msg.messageId}>
                              #{msg.messageId} - {msg.section}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    {(['variable', 'calculate', 'redirect', 'email', 'webhook', 'analytics'].includes(actionType)) && (
                      <textarea
                        value={parameters}
                        onChange={(e) => setParameters(e.target.value)}
                        placeholder={`Enter ${actionTypes.find(t => t.value === actionType)?.parameters}`}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                        rows="2"
                      />
                    )}
                    
                    <button
                      onClick={buildAction}
                      disabled={!actionType || !parameters}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <ArrowRight className="h-4 w-4" />
                      Add Action
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Examples */}
          <div>
            <h3 className="font-medium mb-3">Quick Examples</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Single Actions */}
              {actionTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setBuiltAction(type.example)}
                  className="text-left text-sm bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded border font-mono"
                >
                  {type.example}
                </button>
              ))}
            </div>
            
            <h4 className="font-medium mt-4 mb-2">Combined Actions</h4>
            <div className="grid grid-cols-1 gap-2">
              {combinedActions.map((example, index) => (
                <button
                  key={index}
                  onClick={() => setBuiltAction(example)}
                  className="text-left text-sm bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded border font-mono"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(builtAction)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Save Action
          </button>
        </div>
      </div>
    </div>
  );
};